/* eslint-disable no-console */
import _get from 'lodash/get';
import _merge from 'lodash/merge';
import { Lightning, Router } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { LAYOUT_ENTITIES } from '@bedrock/fido-sdk';
import { TRANSPARENT } from '../tornado/utils/colors';
import { dispatchItemAction } from '../cta/cta.actions';
import Spinner from '../tornado/spinner/spinner.component';
import { NextVideo } from './nextVideo/nextVideo.component';
import { Player } from '../player/player.component';
import { LBanner } from './lBanner/lBanner.component';
import { VideoControls } from './controls/controls.component';
import {
  SEEK_SIGNAL,
  PAUSE_SIGNAL,
  TOP_BAR_ITEM_SELECTED_SIGNAL,
  PLAYING_STATE,
  PAUSED_STATE,
  SIDE_GLASS_ITEM_SELECTED_SIGNAL,
  CONTROLS_HIDE_SIGNAL,
  CONTROLS_SHOW_SIGNAL,
} from '../player/interface/controls/controls.constants';
import {
  AUDIO_TRACK_SELECTED_SIGNAL,
  SUBTITLES_TRACK_SELECTED_SIGNAL,
} from '../player/interface/tracks/tracks.constants';
import { CLOSE_SIGNAL, REPLAY_VIDEO_SIGNAL, NEXT_VIDEO_SIGNAL } from './nextVideo/nextVideo.constants';
import connect from './video.connector';
import { getInitialStyle } from './video.style';
import { getTopBarItems } from './video.utils';
import { TOP_BAR_ITEMS } from './video.constants';
import { ContentAdvisory } from '../player/interface/contentAdvisory/contentAdvisory.component';
import { CsaIcon } from '../player/interface/csaIcon/csaIcon.component';
import {
  PAUSE_AD_STATE,
  PLAYBACK_STATE_PAUSED,
  PLAYBACK_STATE_PLAYING,
  reset,
} from '../../logic/player/player.constants';
import { fetchLayout } from '../../logic/layout/layout.actions';
import {
  fetchPlayerLayout,
  refreshPauseAd,
  selectAudioTrack,
  selectSubtitlesTrack,
} from '../../logic/player/player.actions';
import { sendEvent } from '../../logic/analytics/analytics.utils';
import { areForcedSubtitlesOnly } from '../player/interface/tracks/tracks.utils';
import { PauseAds } from '../player/pause-ads/pauseAds.component';
import { DISPLAY_SIGNAL, HIDE_SIGNAL } from '../player/pause-ads/pauseAds.constants';
import { getLBannerWidthScale, getLBannerWidth } from './lBanner/lBanner.utils';
import { Subtitles } from '../subtitles/subtitles.component';

export class Video extends Lightning.Component {
  static _template() {
    return _merge(
      {
        LBanner: {
          type: LBanner,
        },
        Wrapper: {
          Spinner: {
            type: Spinner,
          },
          Subtitles: {
            type: Subtitles,
          },
          Controls: {
            type: VideoControls,
            topBarItems: [],
            signals: {
              [SEEK_SIGNAL]: '_handleSeek',
              [PAUSE_SIGNAL]: '_handleProgressBarLongSeek',
              [TOP_BAR_ITEM_SELECTED_SIGNAL]: '_handleTopBarItemSelection',
              [SIDE_GLASS_ITEM_SELECTED_SIGNAL]: '_handleItemAction',
              [AUDIO_TRACK_SELECTED_SIGNAL]: '_handleAudioTrackSelection',
              [SUBTITLES_TRACK_SELECTED_SIGNAL]: '_handleSubtitlesTrackSelection',
              [CONTROLS_SHOW_SIGNAL]: '_onControlsShown',
              [CONTROLS_HIDE_SIGNAL]: '_onControlsHidden',
            },
          },
          Player: {
            type: Player,
          },
          ContentAdvisory: {
            type: ContentAdvisory,
          },
          CsaIcon: {
            type: CsaIcon,
          },
          PauseAds: {
            type: PauseAds,
            visible: false,
            signals: {
              [HIDE_SIGNAL]: '_handlePauseAdsAutoHide',
              [DISPLAY_SIGNAL]: '_handlePauseAdsDisplay',
            },
          },
          NextVideo: {
            type: NextVideo,
            signals: {
              [CLOSE_SIGNAL]: '_handleNextVideoClose',
              [REPLAY_VIDEO_SIGNAL]: '_handleVideoReplaySelection',
              [NEXT_VIDEO_SIGNAL]: '_handleItemAction',
            },
          },
        },
      },
      getInitialStyle(),
    );
  }

  static _states() {
    return [
      class Loading extends this {},
      class Playback extends this {
        static _states() {
          return [
            class Playing extends this {
              $enter(states) {
                const previousState = _get(states, 'prevState', '');
                // According to requirements, the Content Advisory component should be displayed "after each break"
                this.tag('ContentAdvisory').start();

                if (!this._initialControlsShown) {
                  // First playback in this component's lifecycle: reveal the controls once.
                  this._initialControlsShown = true;
                  this.tag('Controls').showControls();
                } else if (previousState === 'Playback.Paused') {
                  // When player is switching to Playing state from Paused it means, a Play button was triggered and controls were shown
                  // According to the requirements, Content Advisory should be hidden when controls are visible
                  this.tag('ContentAdvisory').pause();
                }
                this.isSeeking = false;
                this.displayPauseAds = false;
              }

              $exit() {
                this.tag('ContentAdvisory').pause();
              }

              _handleEnter() {
                this._handlePauseControl();

                return true;
              }

              _handlePause() {
                this._handlePauseControl();

                return true;
              }

              _handlePlayPause() {
                this._handlePauseControl();

                return true;
              }

              _handlePauseControl() {
                this._pause();
                this.displayPauseAds = true;
              }

              _handleProgressBarLongSeek() {
                this.isSeeking = true;
                this._pause();
              }

              _onControlsHidden() {
                this.tag('ContentAdvisory').resume();

                super._onControlsHidden();
              }

              set playbackState(value) {
                if (value === PLAYBACK_STATE_PAUSED) {
                  this._setState('Playback.Paused');
                }
              }
            },
            class Paused extends this {
              $exit() {
                this.tag('Controls').visible = true;
              }

              _getFocused() {
                if (this.isSeeking) {
                  return super._getFocused();
                }

                const pauseAds = this.tag('PauseAds');

                return pauseAds.isDisplaying ? pauseAds : super._getFocused();
              }

              _handleEnter() {
                this._play();

                return true;
              }

              _handlePlay() {
                this._play();

                return true;
              }

              _handlePlayPause() {
                this._play();

                return true;
              }

              _handlePauseAdsAutoHide() {
                this.displayPauseAds = false;
              }

              _handlePauseAdsDisplay() {
                this.tag('Controls').visible = false;
                this._refocus();
              }

              _play() {
                super._play();

                this.tag('Controls').showControls();
              }

              set pauseAdState(value) {
                if (value === PAUSE_AD_STATE.ERROR) {
                  this.tag('PauseAds').visible = false;
                }
              }
            },
          ];
        }

        set displayPauseAds(value) {
          const pauseAds = this.tag('PauseAds');
          const controls = this.tag('Controls');

          if (!pauseAds.isAvailable || this.shouldDisplayLBanner) {
            controls.visible = true;
            pauseAds.visible = false;

            return;
          }

          if (value) {
            Store.dispatch(refreshPauseAd());
          }

          // the controls stay visible until the pause ad image is displayed
          controls.visible = true;
          pauseAds.visible = value;
        }

        set isVideoEnding(value) {
          this._isVideoEnding = value;
          this._displayContentAdvisoryIfRequired();
        }

        _getFocused() {
          return this.tag('Controls');
        }

        _onControlsShown() {
          this.tag('ContentAdvisory').pause();
          this._isShowingControls = true;
          this.tag('CsaIcon').handleIcon(false);
        }

        _onControlsHidden() {
          this._isShowingControls = false;
          this.tag('CsaIcon').handleIcon(true);
        }

        _displayContentAdvisoryIfRequired() {
          if (this._isVideoEnding && !this._isShowingControls) {
            this.tag('ContentAdvisory').start();
          }
        }
      },
      class Ads extends this {
        _getFocused() {
          return this.tag('Player');
        }
      },
      class NextVideo extends this {
        $enter() {
          this.tag('Controls').visible = false;
        }

        _getFocused() {
          return this.tag('NextVideo');
        }
      },
    ];
  }

  _onUrlParams({ videoId }) {
    Store.dispatch(reset());
    Store.dispatch(fetchLayout(LAYOUT_ENTITIES.video, videoId));
  }

  _active() {
    // Video plays behind the canvas, so nothing may paint over it
    this.stage.setClearColor(TRANSPARENT);
    this._canPlay = false;
    this._hasRelatedVideos = false;
    this._hasTracks = false;
    this._hasProgramInformation = false;
    this._isShowingControls = false;
    this._isShowingLBanner = false;
    this._isVideoEnding = false;
    this._isSeeking = false;
    this._initialControlsShown = false;
    this._setState('Loading');
    this._unsubscribe = this._connect();
    // NOTE: memory clean is used to prevent disappearing image textures on next video screen
    this.stage.gc(true);
  }

  _connect() {
    return connect(this);
  }

  _inactive() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }

  set isLoading(value) {
    this.tag('Spinner').setSmooth('alpha', value ? 1 : 0);
  }

  set canPlay(value) {
    this._canPlay = value;

    if (this._canPlay) {
      this._handleVideoStart();
    }
  }

  set playbackState(value) {
    if (value === PLAYBACK_STATE_PLAYING) {
      this._setState('Playback.Playing');
    }
  }

  set hasRelatedVideos(value) {
    this._hasRelatedVideos = value;
    this._updateTopBarItems();
  }

  set audioTracks(value) {
    this._hasTracks = this._hasTracks || (value && value.length > 1);
    this._updateTopBarItems(value);
  }

  set subtitleTracks(value) {
    this._hasTracks = this._hasTracks || (value && value.length > 0 && !areForcedSubtitlesOnly(value));
    this._updateTopBarItems(value);
  }

  set shouldDisplayNextVideo(value) {
    const nextVideo = this.tag('NextVideo');
    nextVideo.visible = value && nextVideo.isAvailable;

    if (value) {
      if (nextVideo.isAvailable) {
        this._setState('NextVideo');
      } else {
        Router.back();
      }
    }
  }

  get shouldDisplayLBanner() {
    return this._isLBannerEnabled;
  }

  set shouldDisplayLBanner(value) {
    this._isLBannerEnabled = Boolean(value);
    this._updateTopBarItems();
    if (value) {
      const playerScale = 1 - getLBannerWidthScale();
      const playerX = getLBannerWidth();
      this.tag('Player').setLBannerState(true);
      this.tag('Wrapper').patch({ smooth: { scale: playerScale, x: playerX } });
      this.tag('LBanner').show();
    } else {
      this.tag('Player').setLBannerState(false);
      this.tag('Wrapper').patch({ smooth: { scale: 1, x: 0 } });
      this.tag('LBanner').hide();
    }
  }

  set programInformation(value) {
    this._hasProgramInformation = Boolean(value && (value.description || value.title));
    this._updateTopBarItems();
  }

  set isAdBreakDisplayed(value) {
    if (value) {
      this._setState('Ads');
    }
  }

  get isSeeking() {
    return this._isSeeking;
  }

  set isSeeking(value) {
    this._isSeeking = value;
  }

  start() {
    Store.dispatch(fetchPlayerLayout());
  }

  _handleVideoStart() {
    this.tag('NextVideo').visible = false;
    this.tag('Player').play();
  }

  _handleBack() {
    sendEvent({ key: 'pressBackButtonDuringVideo' });

    return false;
  }

  _handleStop() {
    Router.back();

    return true;
  }

  _pause() {
    this.tag('Player').pause();
    this.tag('Controls').show(PAUSED_STATE);
  }

  _play() {
    this.tag('Player').play();
    this.tag('Controls').show(PLAYING_STATE);
  }

  _updateTopBarItems() {
    this.tag('Controls').topBarItems = getTopBarItems({
      hasTracks: this._hasTracks,
      hasRelatedVideos: this._hasRelatedVideos,
      hasProgramInformation: this._hasProgramInformation,
      hasStartOver: !this._isLBannerEnabled,
    });
  }

  _handleSeek(position) {
    console.log('[video.component] Content position on seek:', position);

    this.tag('Player').seek(position);
  }

  _handleTopBarItemSelection(item) {
    const id = _get(item, 'id', '');

    if (id === TOP_BAR_ITEMS.BACK) {
      Router.back();
    } else if (id === TOP_BAR_ITEMS.START_OVER) {
      this._handleSeek(0);
      this._play();
    } else if (id === TOP_BAR_ITEMS.TRACKS) {
      this.tag('Controls').showTracks();
    } else if (id === TOP_BAR_ITEMS.RELATED_VIDEOS) {
      this.tag('Controls').showSideGlass();
    } else if (id === TOP_BAR_ITEMS.INFO_PAGE) {
      this.tag('Controls').showProgramInformation();
    }
  }

  _handleItemAction(item) {
    dispatchItemAction(item);
  }

  _handleAudioTrackSelection(track) {
    if (track) {
      Store.dispatch(selectAudioTrack(track));
    }
  }

  _handleSubtitlesTrackSelection(track) {
    Store.dispatch(selectSubtitlesTrack(track));
  }

  _handleVideoReplaySelection() {
    this.tag('NextVideo').visible = false;
    this.tag('Player').replay();
  }

  _handleNextVideoClose() {
    Router.back();
  }
}
